import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import * as React from 'react';
import { Platform, ScrollView, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useIncidental } from '@/api/incidentals/use-incidental';
import { useReservation } from '@/api/reservations/use-reservation';
import BackButton from '@/components/back-button';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { formatIsoInZone, isDesktop, snakeToTitleCase } from '@/core';
import { Text } from '@/ui';
import { Loader } from '@/ui/loader';

import MediaPreviewer from './media-previewer';

export interface IncidentalProps {
  pictures: { name: string; url: string }[];
  pdfs: { name: string; data: string }[];
}

export default function Incidental({}: IncidentalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const local = useLocalSearchParams<{ id: string; inc_id: string }>();
  const [mediaViewerVisible, setMediaViewerVisible] = React.useState(false);

  const {
    data: incidental,
    isPending,
    isFetching,
  } = useIncidental({
    //@ts-ignore
    variables: { id: local.inc_id },
  });

  const { data: reservation } = useReservation({
    //@ts-ignore
    variables: { id: local.id },
    enabled: !!local.id,
  });

  // Render the offense date in the reservation's pickup location zone so it
  // doesn't shift to the viewer's local time.
  const pickupTz = reservation?.pickupLocation?.timezone || 'UTC';

  const formatDate = (d: string) =>
    d ? formatIsoInZone(d, pickupTz, 'MM.dd.yyyy') : undefined;

  const pictures = React.useMemo(() => {
    return incidental?.images.filter(
      (image) => image.split('.').pop() !== 'pdf'
    );
  }, [incidental]);

  const pdfs = React.useMemo(() => {
    return incidental?.images.filter(
      (image) => image.split('.').pop() === 'pdf'
    );
  }, [incidental]);

  if (isFetching)
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 200, height: 200 }} />
      </View>
    );

  return (
    <ScrollView
      contentContainerStyle={{
        flexGrow: 1,
        gap: 32,
        alignItems: 'center',
      }}
      style={{
        backgroundColor: theme.colors.background_1,
      }}
      showsVerticalScrollIndicator={false}
    >
      {Platform.OS !== 'web' || !isDesktop() ? (
        <Stack.Screen
          options={{
            title:
              incidental &&
              `${formatDate(incidental.offenseDate)} - ${snakeToTitleCase(
                incidental.category
              )}`,
            headerTitle:
              incidental &&
              `${formatDate(incidental.offenseDate)} - ${snakeToTitleCase(
                incidental.category
              )}`,
            headerLeft: () => (
              <BackButton
                onPress={() => {
                  if (mediaViewerVisible) {
                    router.replace(
                      `/account/trip-history/${local.id}/incidentals/${local.inc_id}`
                    );
                    setMediaViewerVisible(false);
                    return;
                  }

                  router.back();
                }}
              />
            ),
          }}
        />
      ) : null}

      <View style={styles.container}>
        {isDesktop() ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                if (mediaViewerVisible) {
                  router.replace(
                    `/account/trip-history/${local.id}/incidentals/${local.inc_id}`
                  );
                  setMediaViewerVisible(false);
                  return;
                }

                router.back();
              }}
            />

            <Text type="largeTitle">
              {`${formatDate(incidental.offenseDate)} - ${snakeToTitleCase(
                incidental.category
              )}`}
            </Text>
          </View>
        ) : null}

        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          style={styles.scrollView}
        >
          <MediaPreviewer
            title={`${formatDate(incidental.offenseDate)} - ${snakeToTitleCase(
              incidental.category
            )}`}
            pictures={pictures}
            pdfs={pdfs}
            onOpen={() => {
              setMediaViewerVisible(true);
            }}
            carouselHeight={isDesktop() ? 700 : undefined}
            carouselWidth={isDesktop() ? 600 : undefined}
          />
        </ScrollView>
      </View>
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    width: {
      xs: '100%',
      md: 'auto',
    },
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 24,
    gap: 16,
    backgroundColor: theme.colors.background_1,
  },
  scrollView: {
    width: '100%',
    height: '100%',
  },
  scrollContent: {
    width: {
      xs: '100%',
      md: 600,
    },
    alignSelf: 'center',
  },
}));
